import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Typography, Spin, Button, DatePicker, Space, Table } from 'antd';
import { FilePdfOutlined, FileWordOutlined } from '@ant-design/icons';
import { LineChart, ComposedChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, LabelList } from 'recharts';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getRevenueAnalysis, getMarketDashboard, getMonthlyReportSummary, getDsmPenalty, getVolumeDashboard, getGDAMAllocationAnalysis } from '../services/api';
import logoImg from '../assets/logo.png';
import { exportMonthlyReportDocx } from '../utils/reportDocxExporter';

const { Title, Text } = Typography;

// Premium color palette
const COLORS = {
  primary: '#0052CC', // Apraava Blue
  secondary: '#27AE60', // Apraava Green
  dark: '#1e293b', // Slate 800
  light: '#f8fafc', // Slate 50
  accent: '#E74C3C', // Accent Red
  border: '#cbd5e1', // Border Grey
};

// Formatting helper for currency in Millions or Lakhs/Thousands
const formatRupees = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '₹ 0.00';
  const num = Number(val);
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1000000) {
    return `${sign}${(absNum / 1000000).toFixed(2)}`;
  }
  if (absNum >= 1000) {
    return `${sign}${(absNum / 1000).toFixed(2)}`;
  }
  return `${sign}${absNum.toFixed(2)}`;
};



const INITIAL_LAYOUT_ITEMS = [
  {
    id: 'exec-summary',
    type: 'text',
    title: 'Executive Summary',
    content: '<p>Operational and financial performance for Andhralake remained robust. Solid volume realizations and minimal DSM penalties contributed to steady net revenue post-dsm.</p><ul><li>MTD Net Revenue achieved targets with favorable exchange prices.</li><li>Gross tariff realization averaged competitively against benchmark rates.</li></ul>',
    page: 1
  },
  {
    id: 'revenue-summary-table',
    type: 'table',
    title: 'Revenue Summary',
    page: 2
  },
  {
    id: 'key-developments',
    type: 'text',
    title: 'Key Developments',
    content: '<ul><li>Completed scheduled seasonal maintenance on major wind turbine clusters ahead of schedule, improving availability.</li><li>Negotiated extension of auxiliary power supply agreements at competitive tariffs.</li></ul>',
    page: 2
  },
  {
    id: 'market-commentary',
    type: 'text',
    title: 'Market Commentary',
    content: '<p>The G-DAM market segments saw healthy liquidity during the month. Spot price volatility remained moderate with price spikes concentrated during peak evening grid hours.</p>',
    page: 2
  },
  {
    id: 'per-unit-chart',
    type: 'chart1',
    title: 'Per Unit Net Revenue Realization',
    page: 3
  },
  {
    id: 'observations',
    type: 'text',
    title: 'Observations',
    content: '<ul><li><strong>Peak Tariff Realization:</strong> Highest realizations were observed during afternoon production peaks.</li><li><strong>DSM Efficiency:</strong> Active scheduling limiters successfully avoided high-rate penalty bands.</li></ul>',
    page: 3
  },
  {
    id: 'gdam-chart',
    type: 'chart2',
    title: '24 Hour Monthly Average DAM Price',
    page: 4
  },
  {
    id: 'additional-notes',
    type: 'text',
    title: 'Additional Notes',
    content: '<p>1. Operational metrics and revenue values presented here are provisional and subject to final monthly settlement sheets.</p><p>2. STU loss coefficients applied are in accordance with the latest regional grid operator notifications.</p>',
    page: 4
  }
];

export default function MonthlyReportPage() {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingWord, setGeneratingWord] = useState(false);
  const [generationTime, setGenerationTime] = useState(null);

  // Summary Metrics State for Section 1 (calculated dynamically for the month)
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalGen: '—',
    totalSched: '—',
    plf: '—',
    bilatVol: '—',
    bilatPct: '—',
    exchangeVol: '—',
    exchangePct: '—',
    revenueYtd: '—',
    revenueMtd: '—',
    revenueThisMonth: '—',
    realizationGross: '—',
    realizationBilatGross: '—',
    realizationExchGross: '—',
    overallExchRealisation: '—',
    realizationCharges: '—',
    realizationDsm: '—',
    realizationPostDsm: '—'
  });

  // Daily Chart Data for Section 2 (Gross Realisation per day of month)
  const [dailyChartData, setDailyChartData] = useState([]);

  // DAM Snapshot States for Page 2
  const [damYearlySnapshot, setDamYearlySnapshot] = useState([]);
  const [damDailySnapshot, setDamDailySnapshot] = useState([]);

  // Dynamic Data States (Hardcoded to plant_id = 1 as per requirements)
  const [reportData, setReportData] = useState({
    mtdRaw: [],
    ytdRaw: [],
    fytdRaw: [],
    exchangeRaw: [],
    aggregated: {
      mtd: null,
      ytd: null,
      fytd: null,
    }
  });

  // Layout Items for monthly report
  const [layoutItems, setLayoutItems] = useState(INITIAL_LAYOUT_ITEMS);

  // Fetch Report Data on month change
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const plantIdParam = 1; // Hardcoded to Andhralake (Plant 1)

      const today = dayjs();
      const yesterday = today.subtract(1, 'day');
      const monthStart = selectedMonth.startOf('month');
      const monthEnd = selectedMonth.endOf('month');
      const isCurrentMonth = selectedMonth.isSame(today, 'month');
      const isFutureMonth = selectedMonth.isAfter(today, 'month');

      const mStart = monthStart.format('YYYY-MM-DD');
      let mEnd = monthEnd.format('YYYY-MM-DD');
      if (isCurrentMonth) {
        mEnd = yesterday.isBefore(monthStart) ? mStart : yesterday.format('YYYY-MM-DD');
      } else if (isFutureMonth) {
        mEnd = mStart;
      }

      const selectedYear = selectedMonth.year();
      const selectedMonthNum = selectedMonth.month() + 1;
      const fyYear = selectedMonthNum >= 4 ? selectedYear : selectedYear - 1;
      const fyStart = `${fyYear}-04-01`;

      const yearStart = `${selectedYear}-01-01`;
      let yearEnd = `${selectedYear}-12-31`;
      if (selectedYear === today.year()) {
        yearEnd = yesterday.isBefore(dayjs(`${selectedYear}-01-01`))
          ? `${selectedYear}-01-01`
          : yesterday.format('YYYY-MM-DD');
      } else if (selectedYear > today.year()) {
        yearEnd = `${selectedYear}-01-01`;
      }

      const dsmParams = { plant_id: plantIdParam, date_from: mStart, date_to: mEnd, granularity: '15min' };
      const volParams = { plant_id: plantIdParam, date_from: mStart, date_to: mEnd, granularity: 'daily' };
      const mtdParams = { plant_id: plantIdParam, date_from: mStart, date_to: mEnd, granularity: 'daily' };
      const ytdParams = { plant_id: plantIdParam, date_from: yearStart, date_to: mEnd, granularity: 'monthly' };
      const fytdParams = { plant_id: plantIdParam, date_from: fyStart, date_to: mEnd, granularity: 'monthly' };
      const exchangeHourlyParams = { plant_id: plantIdParam, date_from: mStart, date_to: mEnd, granularity: 'hourly' };
      const exchangeYearlyParams = { plant_id: plantIdParam, date_from: yearStart, date_to: yearEnd, granularity: 'monthly' };
      const exchangeDailyParams = { plant_id: plantIdParam, date_from: mStart, date_to: mEnd, granularity: 'daily' };

      const [resDsm, resVolume, resMTD, resYTD, resFYTD, resExchHourly, resExchYearly, resExchDaily, resSummary, resAnalysis] = await Promise.all([
        getDsmPenalty(dsmParams).catch(err => {
          console.warn('getDsmPenalty failed', err);
          return { data: { chart_data: [] } };
        }),
        getVolumeDashboard(volParams).catch(err => {
          console.warn('getVolumeDashboard failed', err);
          return { data: { results: [] } };
        }),
        getRevenueAnalysis(mtdParams).catch(err => {
          console.warn('getRevenueAnalysis MTD failed', err);
          return { data: { results: [] } };
        }),
        getRevenueAnalysis(ytdParams).catch(err => {
          console.warn('getRevenueAnalysis YTD (Calendar Year) failed', err);
          return { data: { results: [] } };
        }),
        getRevenueAnalysis(fytdParams).catch(err => {
          console.warn('getRevenueAnalysis FYTD failed', err);
          return { data: { results: [] } };
        }),
        getMarketDashboard(exchangeHourlyParams).catch(err => {
          console.warn('getMarketDashboard hourly failed', err);
          return { data: { results: [] } };
        }),
        getMarketDashboard(exchangeYearlyParams).catch(err => {
          console.warn('getMarketDashboard yearly failed', err);
          return { data: { results: [] } };
        }),
        getMarketDashboard(exchangeDailyParams).catch(err => {
          console.warn('getMarketDashboard daily failed', err);
          return { data: { results: [] } };
        }),
        getMonthlyReportSummary({ plant_id: plantIdParam, start_date: mStart, end_date: mEnd }).catch(err => {
          console.warn('getMonthlyReportSummary failed', err);
          return null;
        }),
        getGDAMAllocationAnalysis({
          plant_id: plantIdParam,
          start_date: mStart,
          end_date: mEnd,
          what_if_rules: []
        }).catch(err => {
          console.warn('getGDAMAllocationAnalysis failed', err);
          return { data: { daily_situations: {} } };
        })
      ]);

      const dsmList = resDsm.data?.chart_data || [];
      const volumeList = resVolume.data?.results || [];
      const revenueList = resMTD.data?.results || [];

      // 1. DSM Aggregates (Generation & PLF)
      let sumActual = 0;
      let sumSchedule = 0;
      dsmList.forEach(row => {
        sumActual += Number(row.actual) || 0;
        sumSchedule += Number(row.schedule) || 0;
      });

      const totalGenVal = (sumActual * 250.0) / 1000000.0;
      const totalGen = totalGenVal.toFixed(2);
      const totalSchedVal = (sumSchedule * 250.0) / 1000000.0;
      const totalSched = totalSchedVal.toFixed(2);

      const numDays = dayjs(mEnd).diff(dayjs(mStart), 'day') + 1;
      const plf = (((sumActual * 250.0) / (106.4 * 1000.0 * 24.0 * numDays)) * 100.0).toFixed(2);

      // 2. Volume Aggregates (Schedule Generation Breakup)
      let sumGdamKwh = 0, sumDamKwh = 0, sumRtmKwh = 0, sumBilatKwh = 0;
      const volMap = {};
      volumeList.forEach(row => {
        if (row.period) {
          const cleanPeriod = row.period.split(' - ')[0].trim();
          volMap[cleanPeriod] = row;
        }
        sumGdamKwh += Number(row.gdam) || 0;
        sumDamKwh += Number(row.dam) || 0;
        sumRtmKwh += Number(row.rtm) || 0;
        sumBilatKwh += (Number(row.bilat1) || 0) + (Number(row.bilat2) || 0) + (Number(row.bilat3) || 0);
      });

      const bilatVolNum = sumBilatKwh / 1000000.0;
      const exchangeVolNum = (sumGdamKwh + sumDamKwh + sumRtmKwh) / 1000000.0;
      const schedTotal = bilatVolNum + exchangeVolNum;
      const bilatPct = schedTotal > 0 ? ((bilatVolNum / schedTotal) * 100).toFixed(1) + '%' : '0.0%';
      const exchangePct = schedTotal > 0 ? ((exchangeVolNum / schedTotal) * 100).toFixed(1) + '%' : '0.0%';
      const bilatVolStr = bilatVolNum.toFixed(2);
      const exchangeVolStr = exchangeVolNum.toFixed(2);

      // 3. Revenue Aggregates (YTD, MTD, This Month)
      let sumGrossRev = 0, sumNetPre = 0, sumNetPost = 0, sumDsmCharges = 0;
      let sumBilatGross = 0, sumExchGross = 0, sumBilatSchedVol = 0;
      revenueList.forEach(row => {
        sumGrossRev += Number(row.gross_rev) || 0;
        sumNetPre += Number(row.net_pre) || 0;
        sumNetPost += Number(row.net_post) || 0;
        sumDsmCharges += Number(row.total_dsm_charges) || 0;

        const bGross = Number(row.contract_gross_rev) || ((Number(row.gross_goa) || 0) + (Number(row.gross_nidar) || 0) + (Number(row.gross_npcl) || 0));
        sumBilatGross += bGross;

        const eGross = (Number(row.exchange_gross_rev) != null && !isNaN(Number(row.exchange_gross_rev)) && Number(row.exchange_gross_rev) > 0)
          ? Number(row.exchange_gross_rev)
          : ((Number(row.gdam_gross_rev) || 0) + (Number(row.dam_gross_rev) || 0) + (Number(row.rtm_gross_rev) || 0));
        sumExchGross += eGross;

        sumBilatSchedVol += (Number(row.contract_schedule_vol) || 0) || ((Number(row.schedule_vol_goa) || 0) + (Number(row.schedule_vol_nidar) || 0) + (Number(row.schedule_vol_npcl) || 0));
      });

      const totalBilatVolKwh = sumBilatKwh > 0 ? sumBilatKwh : sumBilatSchedVol;
      const totalExchVolKwh = (sumGdamKwh + sumDamKwh + sumRtmKwh);

      const bilatGrossTariff = totalBilatVolKwh > 0 ? (sumBilatGross / totalBilatVolKwh).toFixed(2) : '0.00';
      const exchGrossTariff = totalExchVolKwh > 0 ? (sumExchGross / totalExchVolKwh).toFixed(2) : '0.00';

      // Independently calculate volume-weighted exchange realization for the selected date range
      const overallExchRealisation = totalExchVolKwh > 0 ? (sumExchGross / totalExchVolKwh).toFixed(2) : '0.00';

      const totalGrossRev = (sumGrossRev / 1000000.0).toFixed(2);
      const netRevenuePreDsm = (sumNetPre / 1000000.0).toFixed(2);
      const netRevenuePostDsm = (sumNetPost / 1000000.0).toFixed(2);
      const dsmPenalty = (Math.abs(sumDsmCharges) / 1000000.0).toFixed(2);

      let ytdNetPostSum = 0;
      (resYTD.data?.results || []).forEach(r => {
        ytdNetPostSum += Number(r.net_post) || 0;
      });

      const revenueYtd = (ytdNetPostSum / 1000000.0).toFixed(2);
      const revenueMtd = netRevenuePostDsm;
      const revenueThisMonth = netRevenuePostDsm;

      // 4. Realization (Rs/kWh)
      const kpis = resDsm.data?.kpi_cards || {};
      const dsmPayable = (Number(kpis.net_dsm_payable || 0) / 1000000.0).toFixed(2);
      const dsmReceivable = (Number(kpis.net_dsm_receivable || 0) / 1000000.0).toFixed(2);
      const dsmPenaltyVal = (Number(kpis.dsm_penalty || 0) / 1000000.0).toFixed(2);

      const schedKwh = Number(totalSched) * 1000000.0;
      const actKwh = Number(totalGen) * 1000000.0;
      const baseKwh = schedKwh > 0 ? schedKwh : (actKwh > 0 ? actKwh : 0);

      let realizationGross = '0.00';
      let realizationCharges = '0.00';
      let realizationDsm = '0.00';
      let realizationPostDsm = '0.00';

      if (baseKwh > 0 && sumGrossRev > 0) {
        const dsmAdj = (Number(dsmReceivable) || 0) - (Number(dsmPayable) || 0);
        const grossRevWithAdj = (Number(totalGrossRev) * 1000000.0) + (dsmAdj * 1000000.0);
        const chargesTotal = (Number(totalGrossRev) - Number(netRevenuePreDsm)) * 1000000.0;
        const dsmPenaltyTotal = (Number(dsmPenaltyVal) || Number(dsmPenalty) || 0) * 1000000.0;

        const rGross = (grossRevWithAdj / baseKwh);
        const rCharges = (chargesTotal / baseKwh);
        const rDsm = (dsmPenaltyTotal / baseKwh);
        const rPostDsm = rGross - rCharges - rDsm;

        realizationGross = rGross.toFixed(2);
        realizationCharges = rCharges.toFixed(2);
        realizationDsm = rDsm.toFixed(2);
        realizationPostDsm = rPostDsm.toFixed(2);
      }

      setSummaryMetrics({
        totalGen,
        totalSched,
        plf,
        bilatVol: bilatVolStr,
        bilatPct,
        exchangeVol: exchangeVolStr,
        exchangePct,
        totalGrossRev,
        netRevenuePreDsm,
        dsmPenalty,
        netRevenuePostDsm,
        dsmPayable,
        dsmReceivable,
        dsmPenaltyVal,
        revenueYtd,
        revenueMtd,
        revenueThisMonth,
        realizationGross,
        realizationBilatGross: bilatGrossTariff,
        realizationExchGross: exchGrossTariff,
        overallExchRealisation,
        realizationCharges,
        realizationDsm,
        realizationPostDsm
      });

      // 5. Daily Exchange Gross Realisation Mapping
      const dailyRealisationMap = {};
      revenueList.forEach(row => {
        if (!row.period) return;
        const cleanPeriod = row.period.split(' - ')[0].trim();
        const dStr = dayjs(cleanPeriod).format('YYYY-MM-DD');
        const volRow = volMap[cleanPeriod] || {};
        const exchVol = (Number(volRow.gdam) || 0) + (Number(volRow.dam) || 0) + (Number(volRow.rtm) || 0);
        const exchGross = (Number(row.gdam_gross_rev) || 0) + (Number(row.dam_gross_rev) || 0) + (Number(row.rtm_gross_rev) || 0);
        if (exchVol > 0 && exchGross > 0) {
          dailyRealisationMap[dStr] = Number((exchGross / exchVol).toFixed(2));
        } else if (Number(row.exchange_gross_rev) > 0 && exchVol > 0) {
          dailyRealisationMap[dStr] = Number((Number(row.exchange_gross_rev) / exchVol).toFixed(2));
        } else if (Number(row.gross_rev) > 0 && Number(row.total_schedule_mw) > 0) {
          dailyRealisationMap[dStr] = Number((Number(row.gross_rev) / (Number(row.total_schedule_mw) * 250.0)).toFixed(2));
        } else {
          dailyRealisationMap[dStr] = 0;
        }
      });

      // Daily Ideal Scenario Electricity Realisation Mapping (from scenario analysis)
      const dailySituations = resAnalysis?.data?.daily_situations || {};
      const dailyIdealRealisationMap = {};
      Object.entries(dailySituations).forEach(([dKey, sitObj]) => {
        const ideal = sitObj?.Ideal;
        if (ideal) {
          const elec = Number(ideal.Electricity || 0);
          const sched = Number(ideal['Scheduled kWh'] || ideal['Ideal Scheduled kWh'] || ideal['Actual Downscaled kWh'] || 0);
          if (sched > 0 && elec > 0) {
            dailyIdealRealisationMap[dKey] = Number((elec / sched).toFixed(2));
          }
        }
      });

      const daysInMonth = [];
      const startD = dayjs(mStart);
      const endD = dayjs(mEnd);
      const diffD = endD.diff(startD, 'day');
      for (let i = 0; i <= diffD; i++) {
        const cur = startD.add(i, 'day');
        const dStr = cur.format('YYYY-MM-DD');
        daysInMonth.push({
          date: cur.format('DD'), // day number e.g. 01, 02... 31
          fullDate: dStr,
          dayLabel: cur.format('DD-MMM'),
          value: dailyRealisationMap[dStr] !== undefined ? dailyRealisationMap[dStr] : 0,
          idealValue: dailyIdealRealisationMap[dStr] !== undefined ? dailyIdealRealisationMap[dStr] : null
        });
      }
      setDailyChartData(daysInMonth);

      // 6. DAM Snapshot - Yearly Profile (Monthly aggregation for ongoing year)
      const yearlyList = resExchYearly.data?.results || [];
      const formattedYearly = yearlyList.map(row => {
        const p = row.period || '';
        const purch = Number(((Number(row.dam_purchase_bid_sum) || 0) * 0.00025).toFixed(2));
        const sell = Number(((Number(row.dam_sell_bid_sum) || 0) * 0.00025).toFixed(2));
        const mcp = Number((Number(row.dam_mcp) || 0).toFixed(2));
        return {
          date: p ? dayjs(p).format('MMM') : '',
          fullDate: p,
          dam_purchase_volume: purch,
          dam_sell_volume: sell,
          dam_mcp: mcp
        };
      });
      setDamYearlySnapshot(formattedYearly);

      // 7. DAM Snapshot - Daily Profile (Daily aggregation for selected month)
      const dailyExchList = resExchDaily.data?.results || [];
      const dailyDamMap = {};
      dailyExchList.forEach(row => {
        if (!row.period) return;
        const dStr = row.period.split(' ')[0].trim();
        dailyDamMap[dStr] = {
          dam_purchase_volume: Number(((Number(row.dam_purchase_bid_sum) || 0) * 0.00025).toFixed(2)),
          dam_sell_volume: Number(((Number(row.dam_sell_bid_sum) || 0) * 0.00025).toFixed(2)),
          dam_mcp: Number((Number(row.dam_mcp) || 0).toFixed(2))
        };
      });

      const formattedDailyDam = [];
      for (let i = 0; i <= diffD; i++) {
        const cur = startD.add(i, 'day');
        const dStr = cur.format('YYYY-MM-DD');
        const dayData = dailyDamMap[dStr] || { dam_purchase_volume: 0, dam_sell_volume: 0, dam_mcp: 0 };
        formattedDailyDam.push({
          date: cur.format('DD'),
          fullDate: dStr,
          dayLabel: cur.format('DD-MMM'),
          ...dayData
        });
      }
      setDamDailySnapshot(formattedDailyDam);

      // Support existing multi-page tables/charts
      const processResults = (results) => {
        let totalGross = 0, totalNetPre = 0, totalNetPost = 0, totalCharges = 0, totalScheduleMw = 0, totalGenerationMw = 0;
        (results || []).forEach(r => {
          totalGross += Number(r.gross_rev) || 0;
          totalNetPre += Number(r.net_pre) || 0;
          totalNetPost += Number(r.net_post) || 0;
          totalCharges += Number(r.total_charges) || 0;
          totalScheduleMw += Number(r.total_schedule_mw) || 0;
          totalGenerationMw += Number(r.total_generation_mw) || 0;
        });

        const grossTariff = totalScheduleMw > 0 ? (totalGross / (totalScheduleMw * 250.0)) : 0;
        const netTariffPre = totalScheduleMw > 0 ? (totalNetPre / (totalScheduleMw * 250.0)) : 0;
        const netTariffPost = totalScheduleMw > 0 ? (totalNetPost / (totalScheduleMw * 250.0)) : 0;
        const actualTariff = totalGenerationMw > 0 ? (totalNetPost / (totalGenerationMw * 250.0)) : 0;

        return {
          gross_rev: totalGross,
          net_pre: totalNetPre,
          net_post: totalNetPost,
          total_charges: totalCharges,
          dsm_charges: totalNetPre - totalNetPost,
          gross_tariff: grossTariff,
          net_tariff_pre: netTariffPre,
          net_tariff_post: netTariffPost,
          actual_tariff: actualTariff,
          total_schedule_mw: totalScheduleMw,
          total_generation_mw: totalGenerationMw
        };
      };

      const hasSummaryData = resSummary && resSummary.data && Object.keys(resSummary.data).length > 0;
      setReportData({
        mtdRaw: resMTD.data?.results || [],
        ytdRaw: resYTD.data?.results || [],
        fytdRaw: resFYTD.data?.results || [],
        exchangeRaw: resExchHourly.data?.results || [],
        aggregated: hasSummaryData ? resSummary.data : {
          mtd: processResults(resMTD.data?.results),
          ytd: processResults(resYTD.data?.results),
          fytd: processResults(resFYTD.data?.results),
        }
      });

      setLayoutItems(INITIAL_LAYOUT_ITEMS);
    } catch (err) {
      console.error('Error loading report data:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReportData();
  }, [selectedMonth]);

  // Aggregate 24h Average DAM prices
  const dam24hAverages = useMemo(() => {
    const hourlyData = reportData.exchangeRaw;
    return Array.from({ length: 24 }, (_, h) => {
      const hourStr = String(h).padStart(2, '0') + ':00';
      const matchingRows = hourlyData.filter(row => {
        return row.period && row.period.endsWith(hourStr);
      });
      const avgPrice = matchingRows.length > 0
        ? matchingRows.reduce((sum, r) => sum + (Number(r.dam_mcp) || 0), 0) / matchingRows.length
        : 0;
      const avgPurchaseBid = matchingRows.length > 0
        ? matchingRows.reduce((sum, r) => sum + (Number(r.dam_purchase_bid) || 0), 0) / matchingRows.length
        : 0;
      const avgSellBid = matchingRows.length > 0
        ? matchingRows.reduce((sum, r) => sum + (Number(r.dam_sell_bid) || 0), 0) / matchingRows.length
        : 0;
      const avgMcv = matchingRows.length > 0
        ? matchingRows.reduce((sum, r) => sum + (Number(r.dam_mcv_mw) || 0), 0) / matchingRows.length
        : 0;
      const avgClearedVol = matchingRows.length > 0
        ? matchingRows.reduce((sum, r) => sum + (Number(r.total_exch_cleared_vol_mw) || 0), 0) / matchingRows.length
        : 0;
      return {
        hour: String(h).padStart(2, '0') + ':00',
        price: avgPrice,
        purchaseBid: avgPurchaseBid,
        sellBid: avgSellBid,
        mcv: avgMcv,
        clearedVol: avgClearedVol
      };
    });
  }, [reportData.exchangeRaw]);
  const gdam24hAverages = dam24hAverages;

  // Format chart line data for Per Unit Net Revenue
  const perUnitChartData = useMemo(() => {
    return reportData.mtdRaw.map(row => {
      const d = dayjs(row.period);
      return {
        date: d.isValid() ? d.format('DD-MMM') : row.period,
        gross: Number(row.gross_tariff) || 0,
        netPre: Number(row.net_tariff) || 0,
        netPost: Number(row.actual_tariff) || 0
      };
    }).sort((a, b) => a.date.localeCompare(b.date));
  }, [reportData.mtdRaw]);

  // Table Columns config for preview
  const summaryTableColumns = [
    { title: 'Operational / Financial Metric', dataIndex: 'metric', key: 'metric', width: '34%', render: text => <strong>{text}</strong> },
    { title: 'Month to Date (MTD)', dataIndex: 'mtd', key: 'mtd', align: 'right' },
    { title: 'Year to Date (YTD)', dataIndex: 'ytd', key: 'ytd', align: 'right' },
    { title: 'Fiscal Year to Date (FYTD)', dataIndex: 'fytd', key: 'fytd', align: 'right' }
  ];

  const summaryTableData = useMemo(() => {
    const { mtd, ytd, fytd } = reportData.aggregated;
    if (!mtd || !ytd || !fytd) return [];

    return [
      {
        key: 'gross_rev',
        metric: 'Gross Revenue (M)',
        mtd: formatRupees(mtd.gross_rev),
        ytd: formatRupees(ytd.gross_rev),
        fytd: formatRupees(fytd.gross_rev),
      },
      {
        key: 'charges',
        metric: 'Charges & Commissions (M)',
        mtd: formatRupees(mtd.total_charges),
        ytd: formatRupees(ytd.total_charges),
        fytd: formatRupees(fytd.total_charges),
      },
      {
        key: 'net_pre',
        metric: 'Net Revenue Pre-DSM (M)',
        mtd: formatRupees(mtd.net_pre),
        ytd: formatRupees(ytd.net_pre),
        fytd: formatRupees(fytd.net_pre),
      },
      {
        key: 'dsm_penalty',
        metric: 'DSM Deviation Penalty (M)',
        mtd: formatRupees(mtd.dsm_charges),
        ytd: formatRupees(ytd.dsm_charges),
        fytd: formatRupees(fytd.dsm_charges),
      },
      {
        key: 'net_post',
        metric: 'Net Revenue Post-DSM (M)',
        mtd: formatRupees(mtd.net_post),
        ytd: formatRupees(ytd.net_post),
        fytd: formatRupees(fytd.net_post),
      },
      {
        key: 'gross_tariff',
        metric: 'Gross Realization (₹/kWh)',
        mtd: `${mtd.gross_tariff.toFixed(2)}`,
        ytd: `${ytd.gross_tariff.toFixed(2)}`,
        fytd: `${fytd.gross_tariff.toFixed(2)}`,
      },
      {
        key: 'net_tariff_pre',
        metric: 'Net Realization Pre-DSM (₹/kWh)',
        mtd: `${mtd.net_tariff_pre.toFixed(2)}`,
        ytd: `${ytd.net_tariff_pre.toFixed(2)}`,
        fytd: `${fytd.net_tariff_pre.toFixed(2)}`,
      },
      {
        key: 'net_tariff_post',
        metric: 'Net Realization Post-DSM (₹/kWh)',
        mtd: `${mtd.net_tariff_post.toFixed(2)}`,
        ytd: `${ytd.net_tariff_post.toFixed(2)}`,
        fytd: `${fytd.net_tariff_post.toFixed(2)}`,
      },
      {
        key: 'actual_tariff',
        metric: 'Actual Realization Post-DSM (₹/kWh)',
        mtd: `${mtd.actual_tariff.toFixed(2)}`,
        ytd: `${ytd.actual_tariff.toFixed(2)}`,
        fytd: `${fytd.actual_tariff.toFixed(2)}`,
      }
    ];
  }, [reportData.aggregated]);

  const monthYearLabel = selectedMonth.format('MMMM YYYY');

  // Trigger PDF Download
  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    // Set fixed generation timestamp
    const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
    setGenerationTime(now);

    // Wait slightly to ensure rendering updates complete
    await new Promise((r) => setTimeout(r, 600));

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageIds = ['pdf-page-1', 'pdf-page-2'];

      for (let i = 0; i < pageIds.length; i++) {
        const pageEl = document.getElementById(pageIds[i]);
        if (!pageEl) continue;

        const canvas = await html2canvas(pageEl, {
          scale: 2.5, // Crisp rendering scale
          useCORS: true,
          logging: false,
          scrollY: 0,
          scrollX: 0,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        // Add page break if there's a next page
        if (i < pageIds.length - 1) {
          pdf.addPage();
        }
      }

      pdf.save(`Apraava_Monthly_Performance_Report_${selectedMonth.format('MMM_YYYY')}.pdf`);
    } catch (err) {
      console.error('PDF Generation failed:', err);
    }
    setGeneratingPdf(false);
  };

  const handleDownloadWord = async () => {
    setGeneratingWord(true);
    try {
      // Pause slightly to ensure charts are fully settled
      await new Promise((r) => setTimeout(r, 450));

      const images = {};
      const ids = {
        chart1: 'monthly-chart-section-3',
        chart2: 'monthly-chart-section-4',
      };

      for (const [key, id] of Object.entries(ids)) {
        const el = document.getElementById(id);
        if (el) {
          try {
            const canvas = await html2canvas(el, {
              scale: 2.0,
              useCORS: true,
              logging: false,
            });
            images[key] = canvas.toDataURL('image/png');
          } catch (canvasErr) {
            console.error(`Failed to capture monthly chart element #${id}:`, canvasErr);
          }
        }
      }

      await exportMonthlyReportDocx(
        monthYearLabel,
        layoutItems,
        summaryTableData,
        perUnitChartData,
        gdam24hAverages,
        images
      );
    } catch (err) {
      console.error('Monthly Report Word Generation failed:', err);
    }
    setGeneratingWord(false);
  };



  // Repeated Header component
  const ReportHeader = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `2px solid ${COLORS.primary}`,
      paddingBottom: 8,
      marginBottom: 20,
      width: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src={logoImg} alt="Apraava Logo" style={{ height: 26, width: 'auto' }} />
      </div>
    </div>
  );

  // Repeated Footer component
  const ReportFooter = ({ pageNum, totalPages }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderTop: `1px solid ${COLORS.border}`,
      paddingTop: 8,
      marginTop: 20,
      width: '100%',
      fontSize: 8,
      color: '#64748b',
      fontWeight: 500
    }}>
      <div>CONFIDENTIAL · APRAAVA ENERGY</div>
      <div>Generated on: {generationTime || dayjs().format('YYYY-MM-DD HH:mm:ss')}</div>
      <div style={{ fontWeight: 600, color: COLORS.primary }}>Page {pageNum} of {totalPages}</div>
    </div>
  );

  // Render list of layout items assigned to a specific page
  const renderItemsForPage = (pageNum) => {
    return layoutItems
      .filter(item => item.page === pageNum)
      .map(item => {
        if (item.type === 'text') {
          return (
            <div key={item.id} className="report-section">
              <div className="report-section-title">{item.title}</div>
              <div className="rich-text-content" dangerouslySetInnerHTML={{ __html: item.content }} />
            </div>
          );
        }
        if (item.type === 'table') {
          return (
            <div key={item.id} className="report-section" style={{ marginTop: 16 }}>
              <div className="report-section-title">{item.title}</div>
              <div className="report-table">
                <table>
                  <thead>
                    <tr>
                      {summaryTableColumns.map(col => (
                        <th key={col.key} style={{ textAlign: col.align || 'left', width: col.width }}>{col.title}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summaryTableData.map(row => (
                      <tr key={row.key}>
                        <td><strong>{row.metric}</strong></td>
                        <td style={{ textAlign: 'right' }}>{row.mtd}</td>
                        <td style={{ textAlign: 'right' }}>{row.ytd}</td>
                        <td style={{ textAlign: 'right' }}>{row.fytd}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (item.type === 'chart1') {
          return (
            <div key={item.id} className="report-section" style={{ marginTop: 50 }}>
              <div className="report-section-title">{item.title}</div>
              <div id="monthly-chart-section-3" style={{ width: '100%', height: 280, margin: '8px 0' }}>
                {perUnitChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={perUnitChartData} margin={{ top: 10, right: 10, left: 30, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#64748b" />
                      <YAxis tick={{ fontSize: 9 }} stroke="#64748b" label={{ value: 'Tariff Realization (₹/kWh)', angle: -90, position: 'insideLeft', fontSize: 9, style: { fill: '#64748b' } }} />
                      <Tooltip
                        contentStyle={{ fontSize: 10 }}
                        formatter={(value, name) => {
                          const formattedVal = value !== null && value !== undefined && !isNaN(value) ? `₹${Number(value).toFixed(2)}/kWh` : '₹0.00/kWh';
                          let displayName = name;
                          if (name.includes('Gross Realization')) {
                            displayName = 'Gross Realization';
                          } else if (name.includes('Pre DSM')) {
                            displayName = 'Net Realization - Pre-DSM';
                          } else if (name.includes('Post DSM')) {
                            displayName = 'Net Realization - Post-DSM';
                          }
                          return [formattedVal, displayName];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 9, paddingTop: 6 }} />
                      <Line isAnimationActive={false} type="monotone" dataKey="gross" name="Gross Realization (₹/kWh)" stroke="#0052CC" strokeWidth={2} dot={false} />
                      <Line isAnimationActive={false} type="monotone" dataKey="netPre" name="Net Realization - Pre DSM (₹/kWh)" stroke="#27AE60" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                      <Line isAnimationActive={false} type="monotone" dataKey="netPost" name="Net Realization - Post DSM (₹/kWh)" stroke="#E74C3C" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', paddingTop: 80, color: '#94a3b8' }}>No daily data points available.</div>
                )}
              </div>
            </div>
          );
        }
        if (item.type === 'chart2') {
          return (
            <div key={item.id} className="report-section" style={{ marginBottom: 40, marginTop: 24 }}>
              <div className="report-section-title">{item.title}</div>
              <div id="monthly-chart-section-4" style={{ width: '100%', height: 280, margin: '8px 0' }}>
                {dam24hAverages.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={dam24hAverages} margin={{ top: 10, right: 10, left: 10, bottom: 15 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="hour"
                        tick={{ fontSize: 9 }}
                        stroke="#64748b"
                        tickFormatter={(v) => v ? parseInt(v.split(':')[0], 10) : ''}
                        label={{ value: 'Time Period', position: 'insideBottom', offset: -2, fontSize: 9, style: { fill: '#64748b' } }}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 9 }}
                        stroke="#64748b"
                        tickFormatter={(val) => val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val}
                        label={{ value: 'Market Volume (MW)', angle: -90, position: 'insideLeft', offset: -5, fontSize: 9, style: { fill: '#64748b' } }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 9 }}
                        stroke="#64748b"
                        label={{ value: 'MCP (Rs/KWh)', angle: 90, position: 'insideRight', offset: 0, fontSize: 9, style: { fill: '#64748b' } }}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 10 }}
                        formatter={(value, name) => {
                          const numVal = Number(value) || 0;
                          if (name.includes('MCP')) {
                            return [`₹${numVal.toFixed(2)}/kWh`, name];
                          }
                          return [`${numVal.toFixed(2)} MW`, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 9, paddingTop: 10 }} />
                      <Area isAnimationActive={false} yAxisId="left" type="monotone" dataKey="purchaseBid" name="Purchase bid (MW)" stroke="#F39C12" fill="#F4D03F" fillOpacity={0.6} />
                      <Area isAnimationActive={false} yAxisId="left" type="monotone" dataKey="sellBid" name="Total Sell Bid" stroke="#E67E22" fill="#EB984E" fillOpacity={0.6} />
                      <Area isAnimationActive={false} yAxisId="left" type="monotone" dataKey="mcv" name="total MCV" stroke="#27AE60" fill="#58D68D" fillOpacity={0.6} />
                      <Line isAnimationActive={false} yAxisId="left" type="monotone" dataKey="clearedVol" name="Total Cleared Volume" stroke="#8E44AD" strokeWidth={2} dot={false} />
                      <Line isAnimationActive={false} yAxisId="right" type="monotone" dataKey="price" name="MCP (Rs/KWh)" stroke="#333333" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', paddingTop: 80, color: '#94a3b8' }}>No hourly price data available.</div>
                )}
              </div>
            </div>
          );
        }
        return null;
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', background: '#f1f5f9', height: '100vh', fontFamily: 'Inter, sans-serif' }}>

      {/* CSS Stylesheet Inject */}
      <style>{`
        .a4-page-container {
          box-shadow: 0 4px 15px rgba(0,0,0,0.06);
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          background-color: #ffffff;
          width: 794px; /* Exact A4 dimensions at 96dpi */
          height: 1123px;
          padding: 50px 60px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          position: relative;
          margin-bottom: 24px;
          overflow: hidden;
        }
        .report-section {
          margin-bottom: 16px;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .pdf-page-title-block {
          margin: 15px 0 20px 0;
          border-left: 6px solid ${COLORS.primary};
          padding-left: 16px;
        }
        
        /* Sidebar Drag and Drop Styles */
        .sidebar-page-group {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 12px;
          transition: all 0.2s ease;
        }
        .sidebar-page-group.drag-over {
          border-color: ${COLORS.primary} !important;
          background-color: rgba(0, 82, 204, 0.04) !important;
        }
        .sidebar-section-item {
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          background: #ffffff;
          transition: all 0.15s ease;
        }
        .sidebar-section-item.dragging {
          opacity: 0.4;
        }
        .sidebar-section-item.drag-over-before {
          border-top: 2px solid ${COLORS.primary} !important;
        }
        .sidebar-section-item.drag-over-after {
          border-bottom: 2px solid ${COLORS.primary} !important;
        }
        .drag-handle-sidebar {
          cursor: grab;
          font-weight: bold;
          font-size: 14px;
          color: #94a3b8;
          padding: 0 4px;
          user-select: none;
        }
        .drag-handle-sidebar:active {
          cursor: grabbing;
        }
        .report-section-title {
          font-size: 13px;
          font-weight: 700;
          color: ${COLORS.primary};
          text-transform: uppercase;
          border-bottom: 1px solid ${COLORS.border};
          padding-bottom: 4px;
          margin-bottom: 10px;
          letter-spacing: 0.5px;
        }
        .rich-text-content {
          font-size: 11px;
          line-height: 1.6;
          color: #334155;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .rich-text-content p {
          margin-bottom: 8px;
        }
        .rich-text-content ul {
          padding-left: 16px;
          margin-bottom: 10px;
        }
        .rich-text-content li {
          margin-bottom: 4px;
        }
        .rich-text-content h3 {
          font-size: 13px;
          color: ${COLORS.dark};
          margin: 12px 0 6px;
        }
        /* Custom styles for tables within report */
        .report-table table {
          border-collapse: collapse;
          width: 100%;
          font-size: 10px;
        }
        .report-table th {
          background-color: #f8fafc;
          border: 1px solid ${COLORS.border};
          color: #334155;
          font-weight: 700;
          padding: 8px 10px;
          text-align: left;
        }
        .report-table td {
          border: 1px solid ${COLORS.border};
          padding: 7px 10px;
          color: #334155;
        }
        .report-table tr:nth-child(even) {
          background-color: #fafafa;
        }
      `}</style>

      {/* Top Floating Control Bar */}
      <div style={{
        background: '#ffffff',
        borderBottom: '1px solid #cbd5e1',
        padding: '12px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <Space align="center" size={16}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Select Month:</span>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(val) => val && setSelectedMonth(val)}
            format="MMMM YYYY"
            allowClear={false}
            style={{ width: 160 }}
          />
        </Space>

        <Space>
          <Button
            type="primary"
            icon={<FilePdfOutlined />}
            loading={generatingPdf}
            disabled={loading}
            onClick={handleDownloadPdf}
            style={{ background: '#E74C3C', borderColor: '#E74C3C', fontWeight: 600 }}
            title="Download PDF Report"
          >
            PDF
          </Button>
        </Space>
      </div>

      {/* Centered Document Preview Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '30px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>

        {loading ? (
          <div style={{ marginTop: 200, textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#64748b' }}>Fetching report metrics...</div>
          </div>
        ) : (
          <div id="monthly-report-printable-area" className={generatingPdf ? 'pdf-rendering' : ''}>

            {/* ==================== PAGE 1 ==================== */}
            <div id="pdf-page-1" className="a4-page-container">
              <div>
                <ReportHeader />

                {/* Page Title Block */}
                <div className="pdf-page-title-block">
                  <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.primary, lineHeight: 1.1, margin: '4px 0' }}>
                    Monthly Performance Report
                  </h1>
                  <h2 style={{ fontSize: 13, fontWeight: 500, color: '#64748b', margin: 0 }}>
                    {monthYearLabel}
                  </h2>
                </div>

                {/* Section 1: Summary Overview */}
                <div className="report-section" style={{ marginTop: 16 }}>
                  <div className="report-section-title" style={{ fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14, color: '#0f172a' }}>
                    1. SUMMARY OVERVIEW
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', marginBottom: '16px' }}>
                    {/* Card 1: GENERATION OVERVIEW */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        GENERATION OVERVIEW
                      </div>
                      <div style={{ display: 'flex', padding: '14px 18px', alignItems: 'center', minHeight: '80px', boxSizing: 'border-box' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Actual Generation</div>
                          <div style={{ display: 'flex', alignItems: 'baseline' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.totalGen}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginLeft: '4px' }}>MUs</span>
                          </div>
                        </div>
                        <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', margin: '0 16px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>PLF</div>
                          <div>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                              {summaryMetrics.plf !== '—' && summaryMetrics.plf !== null ? `${summaryMetrics.plf}%` : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card 2: Sold(scheduled) Unit breakup */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        Sold(scheduled) Unit breakup
                      </div>
                      <div style={{ display: 'flex', padding: '14px 18px', alignItems: 'center', minHeight: '80px', boxSizing: 'border-box' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Bilateral</div>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.bilatVol}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>MUs</span>
                            <span style={{
                              background: '#e2e8f0',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: 700,
                              color: '#1e293b'
                            }}>
                              {summaryMetrics.bilatPct}
                            </span>
                          </div>
                        </div>
                        <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', margin: '0 16px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Exchange</div>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.exchangeVol}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>MUs</span>
                            <span style={{
                              background: '#e2e8f0',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: 700,
                              color: '#1e293b'
                            }}>
                              {summaryMetrics.exchangePct}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card 3: REVENUE */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        REVENUE
                      </div>
                      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, justifyContent: 'center' }}>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          padding: '7px 14px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          border: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>YTD</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            {summaryMetrics.revenueYtd !== '—' ? `₹ ${summaryMetrics.revenueYtd} M` : '—'}
                          </span>
                        </div>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          padding: '7px 14px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          border: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>This Month</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            {summaryMetrics.revenueThisMonth !== '—' ? `₹ ${summaryMetrics.revenueThisMonth} M` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card 4: PLANT REALIZATION (Rs/Kwh) */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        PLANT REALIZATION (Rs/Kwh)
                      </div>
                      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, justifyContent: 'center' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f1f5f9'
                        }}>
                          <div>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>Gross</span>
                            <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px', fontWeight: 500 }}>
                              (Bilat: {summaryMetrics.realizationBilatGross && summaryMetrics.realizationBilatGross !== '—' ? summaryMetrics.realizationBilatGross : '—'} | Exch: {summaryMetrics.realizationExchGross && summaryMetrics.realizationExchGross !== '—' ? summaryMetrics.realizationExchGross : '—'})
                            </span>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.realizationGross}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Charges & Commission</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            {summaryMetrics.realizationCharges && summaryMetrics.realizationCharges !== '—'
                              ? `- ${String(summaryMetrics.realizationCharges).replace(/^-/, '').trim()}`
                              : '—'}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>DSM Penalty</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            {summaryMetrics.realizationDsm && summaryMetrics.realizationDsm !== '—'
                              ? `- ${String(summaryMetrics.realizationDsm).replace(/^-/, '').trim()}`
                              : '—'}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Revenue Post DSM</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.realizationPostDsm}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Gross Realisation Graph */}
                <div className="report-section" style={{ marginTop: 65 }}>
                  {/* Title & Overall Realisation Header with horizontal line */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    borderBottom: '1.5px solid #cbd5e1',
                    paddingBottom: 6,
                    marginBottom: 16
                  }}>
                    <div style={{
                      fontSize: 15,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: COLORS.primary
                    }}>
                      Gross Realisation (Exchange)
                    </div>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#0f172a'
                    }}>
                      <span style={{ fontWeight: 600, color: '#64748b', marginRight: 6 }}>Overall Realisation:</span>
                      <span style={{ color: COLORS.primary, fontWeight: 800 }}>
                        {summaryMetrics.overallExchRealisation && summaryMetrics.overallExchRealisation !== '—'
                          ? `₹ ${summaryMetrics.overallExchRealisation} / kWh`
                          : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Clean unboxed chart matching report theme */}
                  <div id="monthly-chart-gross-realisation" style={{ width: '100%', height: 260, margin: '8px 0' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={dailyChartData}
                        margin={{ top: 22, right: 15, left: 20, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 9, fill: '#64748b' }}
                          stroke="#64748b"
                          tickLine={{ stroke: '#64748b' }}
                          interval={0}
                        />
                        <YAxis
                          width={48}
                          tick={{ fontSize: 10, fill: '#64748b' }}
                          stroke="#64748b"
                          tickLine={{ stroke: '#64748b' }}
                          domain={[0, (dataMax) => Math.ceil((Number(dataMax) || 4) * 1.25)]}
                          label={{
                            value: 'Tariff Realization (₹/kWh)',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 0,
                            dy: 20,
                            style: { textAnchor: 'middle', fill: '#64748b', fontSize: 10, fontWeight: 500 }
                          }}
                        />
                        <Tooltip
                          formatter={(value, name) => [
                            value !== null && value !== undefined ? `₹ ${Number(value).toFixed(2)} / kWh` : '—',
                            name
                          ]}
                          labelFormatter={(label, items) => {
                            if (items && items[0] && items[0].payload) {
                              return items[0].payload.dayLabel || label;
                            }
                            return label;
                          }}
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            opacity: 1,
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            fontSize: '11px'
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
                        />
                        <Line
                          isAnimationActive={false}
                          type="monotone"
                          dataKey="value"
                          name="Gross Realisation (Exchange)"
                          stroke={COLORS.primary}
                          strokeWidth={2}
                          dot={{ fill: COLORS.primary, stroke: '#ffffff', strokeWidth: 1, r: 2.5 }}
                          activeDot={{ r: 4.5 }}
                        />
                        <Line
                          isAnimationActive={false}
                          type="monotone"
                          dataKey="idealValue"
                          name="Ideal Scenario (Electricity)"
                          stroke="#10B981"
                          strokeWidth={2}
                          dot={{ fill: '#10B981', stroke: '#ffffff', strokeWidth: 1, r: 2.5 }}
                          activeDot={{ r: 4.5 }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

              </div>

              <ReportFooter pageNum={1} totalPages={2} />
            </div>

            {/* ==================== PAGE 2 ==================== */}
            <div id="pdf-page-2" className="a4-page-container" style={{ padding: '28px 48px' }}>
              <div>
                <ReportHeader />

                {/* Page Title Block */}
                <div className="pdf-page-title-block" style={{ margin: '4px 0 16px 0', paddingLeft: 14 }}>
                  <h1 style={{ fontSize: 19, fontWeight: 800, color: COLORS.primary, lineHeight: 1.1, margin: '2px 0' }}>
                    Market Performance & Hourly Insights
                  </h1>
                  <h2 style={{ fontSize: 11, fontWeight: 500, color: '#64748b', margin: 0 }}>
                    DAM Snapshots & 24-Hour GDAM Profile · {monthYearLabel}
                  </h2>
                </div>

                {/* Graph 1: DAM Snapshot - Month-wise for Ongoing Year */}
                <div className="report-section" style={{ marginBottom: 26 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1.5px solid #cbd5e1',
                    paddingBottom: 4,
                    marginBottom: 8
                  }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: COLORS.primary
                    }}>
                      DAM Snapshot - Annual Profile ({selectedMonth.format('YYYY')})
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
                      Monthly Aggregation
                    </div>
                  </div>

                  <div id="monthly-chart-dam-yearly" style={{ width: '100%', height: 245 }}>
                    {damYearlySnapshot.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={damYearlySnapshot} margin={{ top: 18, right: 20, left: 35, bottom: 6 }} barGap={0} barSize={14}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 8.5, fill: '#64748b' }} stroke="#64748b" tickLine={{ stroke: '#64748b' }} />
                          <YAxis
                            yAxisId="left"
                            width={54}
                            tick={{ fontSize: 8.5, fill: '#64748b' }}
                            stroke="#64748b"
                            tickLine={{ stroke: '#64748b' }}
                            label={{
                              value: 'Volume (MU)',
                              angle: -90,
                              position: 'insideLeft',
                              offset: -12,
                              dy: 10,
                              style: { textAnchor: 'middle', fill: '#64748b', fontSize: 9, fontWeight: 600 }
                            }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            width={44}
                            tick={{ fontSize: 8.5, fill: '#64748b' }}
                            stroke="#64748b"
                            tickLine={{ stroke: '#64748b' }}
                            domain={[0, (dataMax) => Math.ceil((Number(dataMax) || 8) * 1.25)]}
                            label={{
                              value: 'MCP (₹/kWh)',
                              angle: 90,
                              position: 'insideRight',
                              offset: 0,
                              dy: 10,
                              style: { textAnchor: 'middle', fill: '#64748b', fontSize: 9, fontWeight: 600 }
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              fontSize: '11px'
                            }}
                            formatter={(value, name) => {
                              const num = Number(value) || 0;
                              if (name.includes('MCP')) return [`₹ ${num.toFixed(2)} / kWh`, name];
                              return [`${num.toLocaleString()} MU`, name];
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 8.5, paddingTop: 4 }} />
                          <Bar isAnimationActive={false} yAxisId="left" dataKey="dam_purchase_volume" name="Purchase Volume (MU)" fill="#0052CC" radius={[2, 0, 0, 0]} barSize={14} />
                          <Bar isAnimationActive={false} yAxisId="left" dataKey="dam_sell_volume" name="Sell Volume (MU)" fill="#FF0000" radius={[0, 2, 0, 0]} barSize={14} />
                          <Line
                            isAnimationActive={false}
                            yAxisId="right"
                            type="monotone"
                            dataKey="dam_mcp"
                            name="MCP (₹/kWh)"
                            stroke="#eab308"
                            strokeWidth={2}
                            dot={{ fill: '#000000', stroke: '#eab308', strokeWidth: 1, r: 2.5 }}
                          >
                            <LabelList
                              dataKey="dam_mcp"
                              position="top"
                              formatter={(val) => (val !== undefined && val !== null && Number(val) > 0 ? Number(val).toFixed(1) : '')}
                              style={{ fill: '#0f172a', fontSize: '7.5px', fontWeight: 700 }}
                            />
                          </Line>
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ textAlign: 'center', paddingTop: 90, color: '#94a3b8', fontSize: 12 }}>No annual DAM snapshot data available.</div>
                    )}
                  </div>
                </div>

                {/* Graph 2: DAM Snapshot - Daily-wise for Selected Month */}
                <div className="report-section" style={{ marginBottom: 26 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1.5px solid #cbd5e1',
                    paddingBottom: 4,
                    marginBottom: 8
                  }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: COLORS.primary
                    }}>
                      DAM Snapshot - Daily Profile ({selectedMonth.format('MMMM YYYY')})
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
                      Daily Aggregation
                    </div>
                  </div>

                  <div id="monthly-chart-dam-daily" style={{ width: '100%', height: 245 }}>
                    {damDailySnapshot.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={damDailySnapshot} margin={{ top: 18, right: 20, left: 35, bottom: 6 }} barGap={0} barSize={6}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 8, fill: '#64748b' }} stroke="#64748b" tickLine={{ stroke: '#64748b' }} interval={0} />
                          <YAxis
                            yAxisId="left"
                            width={54}
                            tick={{ fontSize: 8.5, fill: '#64748b' }}
                            stroke="#64748b"
                            tickLine={{ stroke: '#64748b' }}
                            label={{
                              value: 'Volume (MU)',
                              angle: -90,
                              position: 'insideLeft',
                              offset: -12,
                              dy: 10,
                              style: { textAnchor: 'middle', fill: '#64748b', fontSize: 9, fontWeight: 600 }
                            }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            width={44}
                            tick={{ fontSize: 8.5, fill: '#64748b' }}
                            stroke="#64748b"
                            tickLine={{ stroke: '#64748b' }}
                            domain={[0, (dataMax) => Math.ceil((Number(dataMax) || 8) * 1.25)]}
                            label={{
                              value: 'MCP (₹/kWh)',
                              angle: 90,
                              position: 'insideRight',
                              offset: 0,
                              dy: 10,
                              style: { textAnchor: 'middle', fill: '#64748b', fontSize: 9, fontWeight: 600 }
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              fontSize: '11px'
                            }}
                            formatter={(value, name) => {
                              const num = Number(value) || 0;
                              if (name.includes('MCP')) return [`₹ ${num.toFixed(2)} / kWh`, name];
                              return [`${num.toLocaleString()} MU`, name];
                            }}
                            labelFormatter={(label, items) => {
                              if (items && items[0] && items[0].payload) {
                                return items[0].payload.dayLabel || label;
                              }
                              return label;
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 8.5, paddingTop: 4 }} />
                          <Bar isAnimationActive={false} yAxisId="left" dataKey="dam_purchase_volume" name="Purchase Volume (MU)" fill="#0052CC" radius={[1.5, 0, 0, 0]} barSize={6} />
                          <Bar isAnimationActive={false} yAxisId="left" dataKey="dam_sell_volume" name="Sell Volume (MU)" fill="#FF0000" radius={[0, 1.5, 0, 0]} barSize={6} />
                          <Line
                            isAnimationActive={false}
                            yAxisId="right"
                            type="monotone"
                            dataKey="dam_mcp"
                            name="MCP (₹/kWh)"
                            stroke="#eab308"
                            strokeWidth={1.5}
                            dot={{ fill: '#000000', stroke: '#eab308', strokeWidth: 1, r: 2 }}
                          >
                            <LabelList
                              dataKey="dam_mcp"
                              position="top"
                              offset={4}
                              formatter={(val) => (val !== undefined && val !== null && Number(val) > 0 ? Number(val).toFixed(1) : '')}
                              style={{ fill: '#0f172a', fontSize: '6.5px', fontWeight: 700 }}
                            />
                          </Line>
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ textAlign: 'center', paddingTop: 90, color: '#94a3b8', fontSize: 12 }}>No daily DAM snapshot data available.</div>
                    )}
                  </div>
                </div>

                {/* Graph 3: 24 Hour Monthly Average DAM Price */}
                <div className="report-section" style={{ marginBottom: 0 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1.5px solid #cbd5e1',
                    paddingBottom: 4,
                    marginBottom: 8
                  }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: COLORS.primary
                    }}>
                      24 Hour Monthly Average DAM Price
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
                      Hourly Profile ({selectedMonth.format('MMMM YYYY')})
                    </div>
                  </div>

                  <div id="monthly-chart-section-4" style={{ width: '100%', height: 245 }}>
                    {dam24hAverages.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={dam24hAverages} margin={{ top: 12, right: 20, left: 35, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis
                            dataKey="hour"
                            tick={{ fontSize: 8, fill: '#64748b' }}
                            stroke="#64748b"
                            tickLine={{ stroke: '#64748b' }}
                            tickFormatter={(v) => v ? parseInt(v.split(':')[0], 10) : ''}
                            label={{ value: 'Time Period (Hour)', position: 'insideBottom', offset: -4, fontSize: 8.5, style: { fill: '#64748b', fontWeight: 500 } }}
                          />
                          <YAxis
                            yAxisId="left"
                            width={54}
                            tick={{ fontSize: 8.5, fill: '#64748b' }}
                            stroke="#64748b"
                            tickLine={{ stroke: '#64748b' }}
                            tickFormatter={(val) => val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val}
                            label={{
                              value: 'Market Volume (MW)',
                              angle: -90,
                              position: 'insideLeft',
                              offset: -12,
                              dy: 10,
                              style: { textAnchor: 'middle', fill: '#64748b', fontSize: 9, fontWeight: 600 }
                            }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            width={44}
                            tick={{ fontSize: 8.5, fill: '#64748b' }}
                            stroke="#64748b"
                            tickLine={{ stroke: '#64748b' }}
                            domain={[0, (dataMax) => Math.ceil((Number(dataMax) || 8) * 1.25)]}
                            label={{
                              value: 'MCP (₹/kWh)',
                              angle: 90,
                              position: 'insideRight',
                              offset: 0,
                              dy: 10,
                              style: { textAnchor: 'middle', fill: '#64748b', fontSize: 9, fontWeight: 600 }
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              fontSize: '11px'
                            }}
                            formatter={(value, name) => {
                              const numVal = Number(value) || 0;
                              if (name.includes('MCP')) {
                                return [`₹ ${numVal.toFixed(2)} / kWh`, name];
                              }
                              return [`${numVal.toFixed(2)} MW`, name];
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 8.5, paddingTop: 4 }} />
                          <Area isAnimationActive={false} yAxisId="left" type="monotone" dataKey="purchaseBid" name="Purchase bid (MW)" stroke="#F39C12" fill="#F4D03F" fillOpacity={0.5} />
                          <Area isAnimationActive={false} yAxisId="left" type="monotone" dataKey="sellBid" name="Total Sell Bid (MW)" stroke="#E67E22" fill="#EB984E" fillOpacity={0.5} />
                          <Area isAnimationActive={false} yAxisId="left" type="monotone" dataKey="mcv" name="Total MCV (MW)" stroke="#27AE60" fill="#58D68D" fillOpacity={0.5} />
                          <Line isAnimationActive={false} yAxisId="left" type="monotone" dataKey="clearedVol" name="Total Cleared Vol (MW)" stroke="#8E44AD" strokeWidth={1.5} dot={false} />
                          <Line isAnimationActive={false} yAxisId="right" type="monotone" dataKey="price" name="MCP (₹/kWh)" stroke="#0f172a" strokeWidth={1.8} dot={{ fill: '#0f172a', stroke: '#0f172a', r: 1.5 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ textAlign: 'center', paddingTop: 90, color: '#94a3b8', fontSize: 12 }}>No hourly price data available.</div>
                    )}
                  </div>
                </div>

              </div>

              <ReportFooter pageNum={2} totalPages={2} />
            </div>

          </div>
        )}
      </div>

    </div>
  );
}
